// HUDController — the floating HUD overlay that sits over the fixed
// WebGL canvas. It owns three things:
//
//   1) "What scene are we in?" — answered by subscribing to the
//      useScrollTimeline scene bus.
//   2) "What should be on screen right now?" — answered by reading
//      SCENES[idx].showHUD and rendering the matching HUD elements.
//   3) "Is a panel open?" — answered by listening for the
//      `planet:open-panel` global event and Esc / click-backdrop
//      dismissals.
//
// The controller renders the scene-appropriate HUD chips with smooth
// fade in/out. It does NOT render the planet itself; the planet lives
// in <PlanetScene />.

import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import {
  CONTACT,
  HUD_PANELS,
  NOTES,
  SATELLITES,
  SCENES,
  TIMELINE_MILESTONES,
  type HUDId,
  type SceneConfig,
} from './planetSections';
import { HUDPanel } from './HUDPanel';
import { buildShareUrl } from './useUrlSync';
import { ChapterNav } from './ChapterNav';
import { ChapterPill } from './ChapterPill';
import { ScrollCue } from './ScrollCue';
import { SatelliteInfoCard } from './SatelliteInfoCard';
import { NoteDetailCard } from './NoteDetailCard';
import { Magnetic } from './Magnetic';
import { SceneProgressDots } from './SceneProgressDots';
import { ColorBlockLayer } from './ColorBlockLayer';
import { ModeToggle } from './ModeToggle';
import { SceneTypography } from './SceneTypography';
import './hud.css';

// ---------------------------------------------------------------------------
// Code-split heavy / mode-gated children.
// Each gets its own dynamic chunk + a Suspense boundary with a minimal
// placeholder that matches the component's eventual position so layout
// doesn't shift while the chunk loads.
// ---------------------------------------------------------------------------

const DetailPanel = lazy(() =>
  import('./DetailPanel').then((m) => ({ default: m.DetailPanel })),
);
const AmbientAudioController = lazy(() =>
  import('./AmbientAudioController').then((m) => ({
    default: m.AmbientAudioController,
  })),
);
const TourOverlay = lazy(() =>
  import('./TourOverlay').then((m) => ({ default: m.TourOverlay })),
);
const ExplorationToast = lazy(() =>
  import('./ExplorationToast').then((m) => ({
    default: m.ExplorationToast,
  })),
);
const ExplorationInfoCard = lazy(() =>
  import('./ExplorationInfoCard').then((m) => ({
    default: m.ExplorationInfoCard,
  })),
);

// Minimal, layout-stable placeholders. Invisible-by-default so the HUD
// renders instantly and the chunk paints into a known container.
function PanelSlot() {
  return <div className="hud-lazy-slot hud-lazy-slot--panel" aria-hidden="true" />;
}
function AmbientSlot() {
  return <div className="hud-lazy-slot hud-lazy-slot--ambient" aria-hidden="true" />;
}
function TourSlot() {
  return <div className="hud-lazy-slot hud-lazy-slot--tour" aria-hidden="true" />;
}
function ExplorationToastSlot() {
  return (
    <div className="hud-lazy-slot hud-lazy-slot--explore-toast" aria-hidden="true" />
  );
}
function ExplorationCardSlot() {
  return (
    <div className="hud-lazy-slot hud-lazy-slot--explore-card" aria-hidden="true" />
  );
}

// ---------------------------------------------------------------------------
// Panel-open bus — satellites / fragments dispatch an "open panel"
// event; the controller listens and reflects it in React state.
// ---------------------------------------------------------------------------

const panelOpenListeners = new Set<(id: string | null) => void>();

function emitPanelOpen(id: string | null) {
  if (typeof window !== 'undefined') {
    window.__planetOpenPanelId = id;
  }
  for (const cb of panelOpenListeners) {
    try {
      cb(id);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[HUDController] panel listener threw:', e);
    }
  }
}

// ---------------------------------------------------------------------------
// Hooks — small, scene-aware helpers
// ---------------------------------------------------------------------------

/**
 * useScrollProgress — read window.__planetProgress without causing
 * re-renders. Returns a stable ref-like getter.
 */
function useActiveSceneIndex(initial: number): number {
  const [idx, setIdx] = useState(initial);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIdx(window.__planetSceneIndex ?? 0);
    let last = window.__planetSceneIndex ?? 0;
    const id = window.setInterval(() => {
      const cur = window.__planetSceneIndex ?? 0;
      if (cur !== last) {
        last = cur;
        setIdx(cur);
      }
    }, 200);
    return () => window.clearInterval(id);
  }, []);
  return idx;
}

function useIntroProgress(): number {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;
    const tick = () => {
      const cur = window.__planetProgress ?? 0;
      setP((prev) => (Math.abs(prev - cur) > 0.001 ? cur : prev));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return p;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface HUDControllerProps {
  /** Optional initial chapter — defaults to 0. */
  initialChapter?: number;
}

export function HUDController({ initialChapter = 0 }: HUDControllerProps) {
  const activeIdx = useActiveSceneIndex(initialChapter);
  const [openPanelId, setOpenPanelId] = useState<string | null>(null);
  const [mode, setMode] = useState<'narrative' | 'exploration' | 'tour'>(() => {
    if (typeof window === 'undefined') return 'narrative';
    const v = window.__planetMode;
    return v === 'exploration' || v === 'tour' ? v : 'narrative';
  });
  const [modeShiftKey, setModeShiftKey] = useState(0);

  // ---------------------------------------------------------------------
  // Mode tracking — poll the global mode value so React re-renders when
  // the user toggles via the pill, a keyboard shortcut, or a script.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let last: 'narrative' | 'exploration' | 'tour' = (() => {
      const v = window.__planetMode;
      return v === 'exploration' || v === 'tour' ? v : 'narrative';
    })();
    setMode(last);
    const id = window.setInterval(() => {
      const cur: 'narrative' | 'exploration' | 'tour' = (() => {
        const v = window.__planetMode;
        return v === 'exploration' || v === 'tour' ? v : 'narrative';
      })();
      if (cur !== last) {
        last = cur;
        setMode(cur);
        // Bump a key so we can animate the bg-shift on each toggle.
        setModeShiftKey((n) => n + 1);
      }
    }, 100);
    return () => window.clearInterval(id);
  }, []);

  // ---------------------------------------------------------------------
  // Auto-close any panel when the scene changes.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (openPanelId) {
      setOpenPanelId(null);
      emitPanelOpen(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx]);

  // ---------------------------------------------------------------------
  // Open-panel bus — subscribe + listen for global events.
  // ---------------------------------------------------------------------
  useEffect(() => {
    const cb = (id: string | null) => {
      setOpenPanelId(id);
    };
    panelOpenListeners.add(cb);
    return () => {
      panelOpenListeners.delete(cb);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      setOpenPanelId(detail || null);
      emitPanelOpen(detail || null);
    };
    window.addEventListener('planet:open-panel', onOpen as EventListener);
    return () =>
      window.removeEventListener('planet:open-panel', onOpen as EventListener);
  }, []);

  // ESC closes any open panel.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openPanelId) {
        setOpenPanelId(null);
        emitPanelOpen(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPanelId]);

  const closePanel = useCallback(() => {
    setOpenPanelId(null);
    emitPanelOpen(null);
  }, []);

  // ---------------------------------------------------------------------
  // Derived state.
  // ---------------------------------------------------------------------
  const activeScene: SceneConfig = SCENES[activeIdx] ?? SCENES[0];
  const showHUD = new Set<HUDId>(activeScene.showHUD);
  const isPanelOpen = !!openPanelId;
  const isActiveScenePanelOpen = openPanelId === activeScene.key;
  const isExplore = mode === 'exploration';
  const isTour = mode === 'tour';

  // In tour mode the tour strips the chapter nav + chapter pill + scene
  // dots (the tour strip + scene panel replaces them), and the per-scene
  // HUD panels are forcibly opened (no click required).
  const showChapterNav = !isExplore && !isTour;
  const showChapterPill = !isExplore && !isTour;
  const showSceneProgressDots = !isExplore && !isTour;

  // Share URL for the active scene/mode — fed into each HUDPanel so the
  // copy-link button always reflects what the user is looking at.
  // Memoized on the (idx, mode) pair so we don't churn replaceState
  // every time the controller re-renders for unrelated reasons.
  const shareUrl = buildShareUrl(activeIdx, isExplore);

  return (
    <div
      className={[
        'hud-root',
        isExplore ? 'is-explore' : isTour ? 'is-tour' : 'is-narrative',
        // Bump the key so the bg-shift animation re-fires on every mode toggle.
        `mode-shift-${modeShiftKey}`,
      ]
        .filter(Boolean)
        .join(' ')}
      role="presentation"
      data-mode={mode}
    >
      <ColorBlockLayer />
      <ModeToggle />
      <Suspense fallback={<TourSlot />}>
        <TourOverlay />
      </Suspense>
      <Suspense fallback={<ExplorationToastSlot />}>
        <ExplorationToast />
      </Suspense>
      <Suspense fallback={<ExplorationCardSlot />}>
        <ExplorationInfoCard />
      </Suspense>

      {/* Chapter nav + pill are scene-driven — hide in exploration + tour. */}
      {showChapterNav && <ChapterNav />}
      {showChapterPill && <ChapterPill />}
      {isExplore && <FreeOrbitPill />}

      <ScrollCue isVisible={!isExplore && !isTour && showHUD.has('scroll_cue')} />

      <ChapterLabel
        activeScene={activeScene}
        visible={!isExplore && showHUD.has('chapter_label') && !isTour}
      />

      <IntroKicker
        activeScene={activeScene}
        visible={!isExplore && showHUD.has('intro_kicker') && !isTour}
      />

      <SceneTypography
        scene={activeScene}
        visible={!isExplore && !isTour && activeScene.id > 0}
      />

      {!isExplore && showHUD.has('content_card') && (isActiveScenePanelOpen || isTour) && (
        <AboutPanel
          activeScene={activeScene}
          copyLink={shareUrl}
          onClose={closePanel}
        />
      )}
      {!isExplore && showHUD.has('tag_cloud_card') && (isActiveScenePanelOpen || isTour) && (
        <SkillsPanel
          activeScene={activeScene}
          copyLink={shareUrl}
          onClose={closePanel}
        />
      )}
      {!isExplore && showHUD.has('satellite_info_card') && (
        <SatelliteInfoCard
          activeScene={activeScene}
          openByDefault={
            isActiveScenePanelOpen || isTour
          }
        />
      )}
      {!isExplore && showHUD.has('fragment_list_card') && (
        <NoteDetailCard
          activeScene={activeScene}
          openByDefault={
            isActiveScenePanelOpen || isTour
          }
        />
      )}
      {!isExplore && showHUD.has('milestone_card') && (isActiveScenePanelOpen || isTour) && (
        <TimelinePanel
          activeScene={activeScene}
          copyLink={shareUrl}
          onClose={closePanel}
        />
      )}
      {!isExplore && showHUD.has('contact_card') && (isActiveScenePanelOpen || isTour) && (
        <ContactPanel
          activeScene={activeScene}
          copyLink={shareUrl}
          onClose={closePanel}
        />
      )}

      {/* Backdrop — visible only when a modal panel is open. */}
      <button
        type="button"
        aria-label="Close panel"
        className={['hud-backdrop', isPanelOpen ? 'is-open' : ''].join(' ')}
        onClick={closePanel}
        tabIndex={-1}
      />

      {/* The deep-dive detail overlay — sits on top of everything.
          Lazy-loaded: only fetched on first open, so the deep-dive
          component + its motion/animation code don't tax the initial
          bundle. */}
      <Suspense fallback={<PanelSlot />}>
        <DetailPanel />
      </Suspense>

      {/* Bottom-left scene index dots + bottom-right ambient audio
          toggle. SceneProgressDots is scene-driven — hide in exploration
          and tour. AmbientAudioController lazy-loads the audio
          engine + WebAudio synthesis code on first paint. */}
      {showSceneProgressDots && <SceneProgressDots />}
      <Suspense fallback={<AmbientSlot />}>
        <AmbientAudioController />
      </Suspense>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FreeOrbitPill — replaces ChapterPill in exploration mode on mobile.
// Shows "FREE ORBIT" so the user knows what mode they're in.
// ---------------------------------------------------------------------------

function FreeOrbitPill() {
  return (
    <div
      className="chapter-pill chapter-pill--free-orbit"
      role="group"
      aria-label="Mode indicator"
    >
      <span
        className="chapter-pill__index"
        aria-live="polite"
        aria-atomic="true"
      >
        FREE ORBIT
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// IntroKicker — center card on the Wake scene.
// ---------------------------------------------------------------------------

function IntroKicker({
  activeScene,
  visible,
}: {
  activeScene: SceneConfig;
  visible: boolean;
}) {
  const progress = useIntroProgress();
  if (!visible) return null;
  const fade = Math.max(0, 1 - progress * 1.4);
  const intro = HUD_PANELS.intro;
  return (
    <div
      className="hud-panel hud-panel--center is-open"
      style={{ pointerEvents: 'none', opacity: fade }}
      aria-hidden="true"
    >
      <div className="hud-panel__kicker">{intro.kicker}</div>
      <h1 className="hud-panel__title">{intro.heading}</h1>
      <p className="hud-panel__subtitle">{intro.byline}</p>
      <p className="hud-panel__meta">{intro.meta}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChapterLabel — top-left chapter index + name.
// ---------------------------------------------------------------------------

function ChapterLabel({
  activeScene,
  visible,
}: {
  activeScene: SceneConfig;
  visible: boolean;
}) {
  return (
    <div
      className={['chapter-label', visible ? 'is-open' : ''].join(' ')}
      aria-hidden="true"
    >
      <span className="chapter-label__index">
        Ch. {String(activeScene.id + 1).padStart(2, '0')} / 07
      </span>
      <span className="chapter-label__name">{activeScene.name}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AboutPanel — chapter 1.
// ---------------------------------------------------------------------------

function AboutPanel({
  activeScene,
  copyLink,
  onClose,
}: {
  activeScene: SceneConfig;
  copyLink?: string | null;
  onClose: () => void;
}) {
  const data = HUD_PANELS.about;
  return (
    <HUDPanel
      side="left"
      open
      onClose={onClose}
      ariaLabel="About Yuna"
      readMore={{ type: 'about', id: 'about', label: 'Read more' }}
      copyLink={copyLink}
    >
      <div className="hud-panel__kicker">About</div>
      <h2 className="hud-panel__title">{data.name}</h2>
      <p className="hud-panel__meta">
        {data.meta.location} · {data.meta.stack} · since {data.meta.since}
      </p>
      <p className="hud-panel__body">{data.bio}</p>
      <div className="hud-panel__divider" />
      <div className="hud-panel__tags">
        {data.tags.map((tag) => (
          <Magnetic
            key={tag}
            as="span"
            className="hud-panel__tag"
            innerClassName="hud-panel__tag-inner"
            ariaLabel={`Tag: ${tag}`}
          >
            {tag}
          </Magnetic>
        ))}
      </div>
    </HUDPanel>
  );
}

// ---------------------------------------------------------------------------
// SkillsPanel — chapter 2.
// ---------------------------------------------------------------------------

function SkillsPanel({
  activeScene,
  copyLink,
  onClose,
}: {
  activeScene: SceneConfig;
  copyLink?: string | null;
  onClose: () => void;
}) {
  const data = HUD_PANELS.skills;
  return (
    <HUDPanel
      side="left"
      open
      onClose={onClose}
      ariaLabel="Skills"
      readMore={{ type: 'skills', id: 'skills', label: 'See all' }}
      copyLink={copyLink}
    >
      <div className="hud-panel__kicker">Skills</div>
      <h2 className="hud-panel__title">{activeScene.title}</h2>
      <p className="hud-panel__subtitle">{activeScene.subtitle}</p>
      <div className="hud-panel__divider" />
      {data.categories.map((cat) => (
        <div key={cat.name} style={{ marginBottom: 14 }}>
          <p className="hud-panel__meta" style={{ marginBottom: 6 }}>
            {cat.name}
          </p>
          <div className="hud-panel__tags">
            {cat.items.map((it) => (
              <Magnetic
                key={it}
                as="span"
                className="hud-panel__tag"
                innerClassName="hud-panel__tag-inner"
                ariaLabel={`Skill: ${it}`}
              >
                {it}
              </Magnetic>
            ))}
          </div>
        </div>
      ))}
    </HUDPanel>
  );
}

// ---------------------------------------------------------------------------
// TimelinePanel — chapter 5.
// ---------------------------------------------------------------------------

function TimelinePanel({
  activeScene,
  copyLink,
  onClose,
}: {
  activeScene: SceneConfig;
  copyLink?: string | null;
  onClose: () => void;
}) {
  return (
    <HUDPanel
      side="left"
      open
      onClose={onClose}
      ariaLabel="Timeline"
      readMore={{ type: 'milestone', id: TIMELINE_MILESTONES[0]?.year ?? '2019', label: 'Read story' }}
      copyLink={copyLink}
    >
      <div className="hud-panel__kicker">Timeline</div>
      <h2 className="hud-panel__title">{activeScene.title}</h2>
      <p className="hud-panel__subtitle">{activeScene.subtitle}</p>
      <div className="hud-panel__divider" />
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'grid',
          gap: 12,
        }}
      >
        {TIMELINE_MILESTONES.map((m) => (
          <li
            key={m.year}
            style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}
          >
            <span
              className="t-mono"
              style={{
                color: 'var(--accent-cool)',
                fontSize: 11,
                letterSpacing: '0.2em',
                minWidth: 56,
              }}
            >
              {m.year}
            </span>
            <Magnetic
              as="span"
              className="hud-panel__milestone-row"
              innerClassName="hud-panel__milestone-row-inner"
              ariaLabel={`${m.year}: ${m.label}`}
            >
              {m.label}
            </Magnetic>
          </li>
        ))}
      </ul>
    </HUDPanel>
  );
}

// ---------------------------------------------------------------------------
// ContactPanel — chapter 6.
// ---------------------------------------------------------------------------

function ContactPanel({
  activeScene,
  copyLink,
  onClose,
}: {
  activeScene: SceneConfig;
  copyLink?: string | null;
  onClose: () => void;
}) {
  const items = [
    { label: 'Email', value: CONTACT.email, href: `mailto:${CONTACT.email}`, target: undefined as string | undefined, rel: undefined as string | undefined },
    { label: 'GitHub', value: CONTACT.github.replace(/^https?:\/\//, ''), href: CONTACT.github, target: '_blank', rel: 'noopener noreferrer' },
    { label: 'Mastodon', value: CONTACT.mastodon.replace(/^https?:\/\//, ''), href: CONTACT.mastodon, target: '_blank', rel: 'noopener noreferrer' },
    { label: 'RSS', value: CONTACT.rss, href: CONTACT.rss, target: undefined, rel: undefined },
  ];
  return (
    <HUDPanel
      side="left"
      open
      onClose={onClose}
      ariaLabel="Contact"
      readMore={{ type: 'contact', id: 'contact', label: 'View details' }}
      copyLink={copyLink}
    >
      <div className="hud-panel__kicker">Contact</div>
      <h2 className="hud-panel__title">{activeScene.title}</h2>
      <p className="hud-panel__subtitle">{CONTACT.copy}</p>
      <div className="hud-panel__divider" />
      <ul
        style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}
      >
        {items.map((it) => (
          <li key={it.label} className="hud-panel__contact-row">
            <span className="hud-panel__meta" style={{ marginBottom: 0 }}>
              {it.label}
            </span>
            <Magnetic
              as="a"
              href={it.href}
              target={it.target}
              rel={it.rel}
              className="hud-panel__contact-value"
              innerClassName="hud-panel__contact-value-inner"
              ariaLabel={`${it.label}: ${it.value}`}
            >
              <span>{it.value}</span>
            </Magnetic>
          </li>
        ))}
      </ul>
    </HUDPanel>
  );
}

// Public — re-export the bus so other components (SatelliteMesh, NoteMesh)
// can dispatch open-panel events without an extra import path.
export const openPlanetPanel = (id: string | null) => emitPanelOpen(id);
